import BigNumber from 'bignumber.js';
import {
  CommonFractionDigits,
  MarketCapFractionDigits,
} from '@/lib/config/number';

const subscriptNumbers = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];

function numberToSubscriptNumber(number: number): string {
  const str = number.toString();
  let subscriptNumber = '';
  for (let i = 0; i < str.length; i++) {
    subscriptNumber += subscriptNumbers[+str[i]];
  }
  return subscriptNumber;
}

/**
 * 处理tokenPrice
 * @param price
 * @returns
 */
export function formatPrice(price?: string | number, fixed?: number): string {
  // 处理无效输入
  if (price === undefined || price === null || price === '') return '-';

  try {
    // 如果是数字，先控制精度
    if (typeof price === 'number') price = price.toFixed(20);
    const bn = new BigNumber(price);
    if (bn.isNaN() || bn.isZero()) return '0';

    const abs = bn.abs();

    // 处理大于等于 1 的价格
    if (abs.gte(1)) return abs.toFixed(2);

    // 处理小于 1 的价格
    if (abs.lt(0.0001)) {
      const str = abs.toFixed(20);
      const match = str.match(/^0\.(0*)[1-9]/);
      if (match) {
        const zerosCount = match[1].length;
        const significantPart = str.slice(
          match[0].length - 1,
          match[0].length + 3,
        );
        return `0.0{${zerosCount}}${significantPart}`;
      }
    }

    // 对于一般小数（0.00001 到 1 之间），保留 6 位小数
    return abs.toFixed(fixed ?? 6);
  } catch (error) {
    console.error('Price format error:', error);
    return '0';
  }
}

export function formatCompact(
  number: number,
  maximumFractionDigits?: number,
): string {
  return Intl.NumberFormat(
    'en-US',
    // experimental props
    {
      notation: 'compact',
      roundingMode: 'floor',
      maximumFractionDigits: maximumFractionDigits ?? 1,
    },
  ).format(number);
}

export function commonNumberFormat(number: string | number) {
  if (
    String(number).split('.')[1]?.length <= CommonFractionDigits ||
    String(number).split('.')[1]?.length === undefined
  ) {
    return String(number);
  }
  if (typeof number === 'string') {
    return parseFloat(number).toFixed(CommonFractionDigits);
  }
  return number.toFixed(CommonFractionDigits);
}

/**
 * 处理市值与流动性
 * @param number
 * @returns
 */
export function marketCapValueFormat(number: number | string): string {
  if (typeof number === 'string') {
    return '$' + formatCompact(parseFloat(number), MarketCapFractionDigits);
  }
  return '$' + formatCompact(number, MarketCapFractionDigits);
}

export function abbreviateHashString(hash: string): string {
  return hash.slice(0, 4) + '...' + hash.slice(-4);
}

export function formatPercentage(percentage: number, needSign = true): string {
  if (percentage >= 0 && needSign) {
    return '+' + percentage.toFixed(2) + '%';
  }
  return percentage.toFixed(2) + '%';
}

export function formatUsdAmount(balance?: any): string {
  if (balance === undefined || balance === null || balance === '') return '-';

  const displayBalance = new BigNumber(balance.toString());
  if (displayBalance.isNaN() || !displayBalance.isFinite()) {
    return '-';
  }
  const fixed = displayBalance.abs().lt(1_000_000) ? 2 : 0;
  return displayBalance.toNumber().toLocaleString(undefined, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  });
}

// eslint-disable-next-line default-param-last
export function formatAmount(
  balance?: any,
  decimals = 0,
  fixed?: number,
): string {
  // 处理无效输入
  if (balance === undefined || balance === null || balance === '') return '-';

  const bn = new BigNumber(balance.toString());
  if (bn.isNaN() || !bn.isFinite()) return '-';

  const displayBalance = bn.div(new BigNumber(10).pow(decimals));

  if (displayBalance.abs().lt(1000)) {
    if (displayBalance.eq(0)) {
      fixed ??= 2;
    }
    if (displayBalance.abs().lt(1)) {
      fixed ??= 6;
    }
    fixed ??= 2;
    const result = displayBalance.toFixed(fixed, BigNumber.ROUND_FLOOR);
    return result === '0.000000' ? '0.00' : result;
  }

  if (displayBalance.abs().gte(1000000)) {
    fixed ??= 0;
  }

  fixed ??= 2;
  return displayBalance.toNumber().toLocaleString(undefined, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  });
}

interface FormatPercentOptions {
  prefix?: boolean;
  precision?: number;
  defaultValue?: string;
  max?: number;
  largerSign?: boolean;
}

export function formatPercent(
  number: number | undefined,
  options: FormatPercentOptions = {},
): string {
  const {
    prefix = true,
    precision = 2,
    defaultValue = '--%',
    max = 99.999,
    largerSign = true,
  } = options;

  if (number === undefined) {
    return defaultValue;
  }

  if (number === 0) {
    return '0%';
  }
  const overflow = Math.abs(number) > max;
  const _percent = overflow ? max : number;
  const value = (_percent * 100).toFixed(precision);
  const sign = prefix && _percent > 0 ? '+' : '';
  const largerSignStr = overflow && largerSign ? '>' : '';
  return `${sign}${largerSignStr}${value}%`;
}

export function formatNumber(number: number): string {
  if (number > 1000000000) {
    return `${(number / 1000000000).toFixed(2)}B`;
  } else if (number > 1000000) {
    return `${(number / 1000000).toFixed(2)}M`;
  } else if (number > 1000) {
    return `${(number / 1000).toFixed(2)}K`;
  }
  return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
